<template>
  <div class="min-h-screen bg-primary w-full h-full themeTransition">
    <div v-if="!isLoading" class="sectionContainer place-content-start pt-[100px]">
      <page-wrapper v-if="pageGUI" :page-gui="pageGUI">
        <!-- content begin -->
        <page-header
          :title="page?.title"
          :description="page?.description"
          :theme-type="themeType"
          :isUmbraco="isUmbraco"
          showSlot
        >
          <subpage-navigation
            v-if="navlinks"
            :navlinks="navlinks"
            :activeIndex="activeLinkIdx"
            @setActiveIndex="setActiveLinkIdx"
          />
        </page-header>
        <Transition
          enter-active-class="transition-all duration-500 ease-out-sine"
          leave-active-class="transition-all duration-300 ease-out-sine"
          :enter-from-class="enterDirection"
          :leave-to-class="exitDirection"
        >
          <component
            :is="getComponent()"
            :pageId="activePageId"
            :theme="theme"
            :key="activePageId"
          />
        </Transition>
        <!-- content end -->
      </page-wrapper>
    </div>
  </div>
</template>

<script setup lang="ts">
import { IPageData, Theme } from "@/types/types";
import SubpageNavigation from "@molecules/SubpageNavigation/SubpageNavigation.vue";
import NewsAndPress from "@/views/NewsAndPress.vue";
import SubpageContainer from "@/views/SubpageContainer.vue";
import { parseUrl, getBaseUrl, getThemeType, changeTheme } from "@/util/helper";
import { computed, ref, onBeforeMount, watch } from "vue";
import { getPageWithChildren, getPageFamily } from "@/util/api";
import PageWrapper from "@components/PageWrapper.vue";
import PageHeader from "@atoms/PageHeader/PageHeader.vue";

export interface ActiveLink {
  title: string;
  width: number;
  left: number;
}

const isLoading = ref(true);
const isUmbraco = ref(false);
const pageData = ref<IPageData | null>(null);
const urlParams = ref<String[] | null>(null);
const environment = ref();
const theme = ref<Theme | null>();
const activeLinkIdx = ref(0);
const previousActiveLinkIdx = ref(-1);
const activeRouteId = ref<string>("");
const activePageId = ref<string>();
const pageType = ref<string | undefined>();
const pageGUI = ref<string | undefined>();
const pageId = ref<string | undefined>();

onBeforeMount(async () => {
  pageId.value = document.documentElement.dataset.id;
  environment.value = parseUrl(window.location.href).environment;
  urlParams.value = parseUrl(window.location.href).params;
  if (!urlParams.value) return;
  const parentPage = urlParams.value[0] as string;
  console.log("Parent: ", urlParams.value[0]);
  const subPage = urlParams.value[1] as string;
  console.log("subpage", urlParams.value[1]);

  if (parentPage && pageId.value) {
    pageData.value = await getPageFamily(pageId.value);
    //pageData.value = await getPageWithChildren(parentPage, subPage);
    pageGUI.value = document.documentElement.dataset.parentid;
    const idx = pageData.value?.children.findIndex((child) => child.isQuery);
    setActiveLinkIdx(idx || 0);
  }
  if (pageData.value) {
    console.log("pageData.value: ", pageData.value);
    theme.value = pageData.value?.parent.theme;
    changeTheme(theme.value);
    // need to control the theme of page being set before rendering for both news and subpage components
    isLoading.value = false;
  }
});

const getComponent = () => {
  switch (pageType.value) {
    case "newsCategory":
      return NewsAndPress;
    case "subContentNewsPage":
      return SubpageContainer;
  }
};

const navlinks = computed(() => {
  return pageData.value?.children;
});

function setActiveLinkIdx(index: number) {
  previousActiveLinkIdx.value = activeLinkIdx.value;
  activeLinkIdx.value = index;
}

const activeRoute = computed(() => {
  const activeRoute = navlinks.value?.[activeLinkIdx.value];
  activePageId.value = activeRoute?.guid;
  pageType.value = activeRoute?.pagetype;
  return navlinks.value?.[activeLinkIdx.value];
});

const page = computed(() => {
  return pageData.value?.parent;
});

watch(activeRoute, (value) => {
  if (value && value.url) {
    const BASE_URL = getBaseUrl(environment.value);
    const newUrl = `${BASE_URL}${value.url}`;
    activeRouteId.value = value.guid;
    window.history.pushState({}, "", newUrl);
  }
});

const enterDirection = computed(() => {
  if (previousActiveLinkIdx.value < activeLinkIdx.value) {
    // Moving forward in the list
    return "translate-x-1/2"; // Enter from the right
  } else {
    // Moving backward in the list or no previous index
    return "-translate-x-1/2"; // Enter from the left
  }
});

const exitDirection = computed(() => {
  if (previousActiveLinkIdx.value < activeLinkIdx.value) {
    // Moving forward in the list
    return "-translate-x-full opacity-5"; // Exit to the left
  } else {
    // Moving backward in the list or no previous index
    return "translate-x-full opacity-5"; // Exit to the right
  }
});

const themeType = computed(() => {
  if (!theme.value) return "light";
  return getThemeType(theme.value);
});
</script>
